import { CalendarRange, Download, Plus, Search } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "@/shared/ui/select";

import type { PromotionFilters as Filters } from "../api/query-keys";

import { PrimaryButton, SecondaryButton } from "./buttons";

interface PromotionFiltersProps {
  filters: Filters;
  /** Distinct seller ids from the loaded rows — the BE has no seller-list endpoint. */
  sellers: string[];
  /** Set a single filter; `undefined` clears it (page deletes the key). */
  onChange: <K extends keyof Filters>(key: K, value: Filters[K] | undefined) => void;
  /** Export the currently-visible rows as CSV (client-side). */
  onExport: () => void;
  /** Opens the create-promotion modal — the view's single primary action. */
  onCreate?: () => void;
}

const STATUSES = ["draft", "active", "finish", "archived"] as const;
// Marketplace names are brand proper nouns (matches MarketplaceChip's fixed labels).
const MARKETPLACES: { value: "shopee" | "lazada" | "tiktok"; label: string }[] = [
  { value: "shopee", label: "Shopee" },
  { value: "lazada", label: "Lazada" },
  { value: "tiktok", label: "TikTok" },
];

/** Compact date-range field with a two-input popover (matches the mockup). */
function DateRangeFilter({
  from,
  to,
  onApply,
  onClear,
}: {
  from: string;
  to: string;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}): JSX.Element {
  const { t } = useTranslation("promotion");
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click while open. Draft values are seeded when the popover
  // opens (in the toggle handler) — not in an effect — to avoid cascading renders.
  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  function toggleOpen(): void {
    setOpen((v) => {
      const next = !v;
      if (next) {
        setDraftFrom(from);
        setDraftTo(to);
      }
      return next;
    });
  }

  const hasValue = Boolean(from || to);
  const label = hasValue ? `${from || "…"} → ${to || "…"}` : t("toolbar.date_range_placeholder");

  return (
    <div className="relative" ref={wrapRef} data-testid="filter-date-range">
      <button
        type="button"
        data-testid="filter-date-range-field"
        onClick={toggleOpen}
        className="focus-ring inline-flex h-9 items-center gap-2 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm shadow-[var(--sh-xs)] outline-none transition hover:border-[var(--brand-300)]"
      >
        <CalendarRange aria-hidden className="h-4 w-4 text-[var(--text-subtle)]" />
        <span className={hasValue ? "text-[var(--text-primary)]" : "text-[var(--text-subtle)]"}>
          {label}
        </span>
      </button>

      {open && (
        <div
          data-testid="filter-date-range-popover"
          className="absolute right-0 z-20 mt-1.5 w-72 rounded-xl border border-[var(--premium-border)] bg-white p-3 shadow-[var(--sh-pop)]"
        >
          <div className="grid grid-cols-2 gap-2.5">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-[var(--text-secondary)]">
              {t("filter.date_from")}
              <input
                type="date"
                data-testid="filter-date-from"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="focus-ring h-9 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-2.5 text-sm outline-none transition focus:border-[var(--brand-500)]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-[var(--text-secondary)]">
              {t("filter.date_to")}
              <input
                type="date"
                data-testid="filter-date-to"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                className="focus-ring h-9 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-2.5 text-sm outline-none transition focus:border-[var(--brand-500)]"
              />
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              data-testid="filter-date-clear"
              onClick={() => {
                setDraftFrom("");
                setDraftTo("");
                onClear();
                setOpen(false);
              }}
              className="h-8 rounded-[var(--control-r)] px-3 text-[13px] font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--canvas)]"
            >
              {t("toolbar.date_range_clear")}
            </button>
            <button
              type="button"
              data-testid="filter-date-apply"
              onClick={() => {
                onApply(draftFrom, draftTo);
                setOpen(false);
              }}
              className="focus-ring h-8 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-[13px] font-semibold text-[var(--brand-700)] transition hover:bg-[var(--canvas)]"
            >
              {t("toolbar.date_range_apply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function PromotionFilters({
  filters,
  sellers,
  onChange,
  onExport,
  onCreate,
}: PromotionFiltersProps): JSX.Element {
  const { t } = useTranslation("promotion");

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2.5" data-testid="promotion-filters">
      <div className="relative min-w-[200px] flex-1">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
        />
        <input
          type="search"
          data-testid="promotion-search"
          placeholder={t("toolbar.search_placeholder")}
          value={filters.search ?? ""}
          onChange={(e) => onChange("search", e.target.value || undefined)}
          className="focus-ring h-9 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-10 pr-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition placeholder:text-[var(--text-subtle)] focus:border-[var(--brand-500)]"
        />
      </div>

      <Select
        data-testid="filter-status"
        aria-label={t("column.status")}
        value={filters.status ?? ""}
        onChange={(e) => onChange("status", (e.target.value || undefined) as Filters["status"])}
      >
        <option value="">{t("filter.all_statuses")}</option>
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {t(`status.${s}`)}
          </option>
        ))}
      </Select>

      <Select
        data-testid="filter-marketplace"
        aria-label={t("detail.field.marketplace")}
        value={filters.marketplace ?? ""}
        onChange={(e) =>
          onChange("marketplace", (e.target.value || undefined) as Filters["marketplace"])
        }
      >
        <option value="">{t("filter.all_marketplaces")}</option>
        {MARKETPLACES.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </Select>

      <Select
        data-testid="filter-seller"
        aria-label={t("column.seller")}
        value={filters.seller_id ?? ""}
        onChange={(e) => onChange("seller_id", e.target.value || undefined)}
      >
        <option value="">{t("toolbar.all_sellers")}</option>
        {sellers.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </Select>

      <DateRangeFilter
        from={filters.date_from ?? ""}
        to={filters.date_to ?? ""}
        onApply={(from, to) => {
          onChange("date_from", from || undefined);
          onChange("date_to", to || undefined);
        }}
        onClear={() => {
          onChange("date_from", undefined);
          onChange("date_to", undefined);
        }}
      />

      <SecondaryButton type="button" data-testid="promotion-export" onClick={onExport}>
        <Download className="h-4 w-4" aria-hidden />
        {t("toolbar.export")}
      </SecondaryButton>

      {onCreate && (
        <PrimaryButton type="button" data-testid="promotion-create-button" onClick={onCreate}>
          <Plus className="h-4 w-4" aria-hidden />
          {t("toolbar.create")}
        </PrimaryButton>
      )}
    </div>
  );
}
